/**
 * CIPL document photos live in IndexedDB, not localStorage.
 *
 * Why: every interpack recipient in `recipientPackage` embeds an `interPackDocument`
 * with up to 4 base64 photos. With many recipients that exceeds localStorage quota.
 * So each `interPackDocument` carries a stable `fileKey`; its heavy `*_file` base64
 * fields are split out into IndexedDB (keyed by fileKey) and stored as "" in the row.
 *
 * - On save (both modals):  `await splitInterpackDocument(doc)` → strips + stores files.
 * - On checkout (shipment.js / shipmentLogin.js): `await attachInterpackDocumentFiles(doc)` re-fills.
 * - On edit hydration: `await getCiplFiles(fileKey)`.
 * - A stable fileKey means renumbering/deleting recipients never moves files.
 */

const DB_NAME = 'lionparcel-cipl';
const DB_VERSION = 1;
const STORE_NAME = 'files';
const RECORD_KEY = 'default';

export const FILE_FIELDS = [
  'identity_card_file',
  'tax_number_file',
  'package_before_packing_file',
  'package_after_packing_file',
];

let dbPromise = null;
function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => { dbPromise = null; reject(req.error); };
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = (e) => {
        e.target.result.createObjectStore(STORE_NAME);
      };
    });
  }
  return dbPromise;
}

async function getDb() {
  const db = await openDb();
  return db;
}

/** Read the whole { fileKey → files } record. Never rejects. */
async function readRecord() {
  try {
    const db = await getDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(RECORD_KEY);
      req.onsuccess = () => resolve(req.result || {});
      req.onerror = () => resolve({});
    });
  } catch {
    return {};
  }
}

async function writeRecord(record) {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(record, RECORD_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Random-ish stable key for one recipient's document. */
export function genCiplFileKey() {
  return `cipl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Files object (any of the 4 fields may be present) for a recipient's fileKey. */
export async function getCiplFiles(fileKey) {
  if (!fileKey) return {};
  const record = await readRecord();
  return record[fileKey] || {};
}

/**
 * Synchronously strip heavy base64 fields out of `doc` and move them into IndexedDB
 * (fire-and-forget). Used in sync contexts (Vuex mutations) where we cannot await.
 * Idempotent: already-slim, keyed docs are left untouched.
 */
export function extractCiplFiles(doc) {
  if (!doc) return null;
  const files = {};
  let hasData = false;
  for (const f of FILE_FIELDS) {
    const v = doc[f];
    if (typeof v === 'string' && v.startsWith('data:')) {
      files[f] = v;
      hasData = true;
    }
    doc[f] = '';
  }
  if (!doc.fileKey) doc.fileKey = genCiplFileKey();
  return hasData ? files : null;
}

/** Async version of the split for awaited call sites (modal saves). */
export async function splitInterpackDocument(doc) {
  const files = extractCiplFiles(doc);
  if (files) await setCiplFiles(doc.fileKey, files);
  return doc;
}

/**
 * Synchronous batch splitter for an array of recipient rows — usable inside
 * Vuex mutations (which cannot await). Strips every interPackDocument's heavy
 * `*_file` fields into IndexedDB (fire-and-forget) and assigns a fileKey.
 * Idempotent: slim, already-keyed docs pass through untouched.
 * Returns the same array (rows mutated in place).
 */
export function slimRecipientPackage(rows) {
  if (!Array.isArray(rows)) return rows;
  const pending = [];
  for (const row of rows) {
    const doc = row && row.interPackDocument;
    if (!doc) continue;
    const files = extractCiplFiles(doc);
    if (files) pending.push(setCiplFiles(doc.fileKey, files));
  }
  pending.forEach((p) => p.catch(() => {})); // ponytail: IDB write must not break persist
  return rows;
}

/**
 * Remove the dropped row's files from IndexedDB. Fire-and-forget.
 */
export function dropRecipientFiles(row) {
  const fileKey = row && row.interPackDocument && row.interPackDocument.fileKey;
  if (!fileKey) return;
  removeCiplFiles(fileKey).catch(() => {});
}

/** Store a recipient's files under its fileKey. */
export async function setCiplFiles(fileKey, files) {
  if (!fileKey) return;
  const record = await readRecord();
  record[fileKey] = files || {};
  await writeRecord(record);
}

/** Remove one recipient's files (used when its row is deleted). */
export async function removeCiplFiles(fileKey) {
  if (!fileKey) return;
  const record = await readRecord();
  if (!record[fileKey]) return;
  delete record[fileKey];
  await writeRecord(record);
}

/** Wipe all CIPL files (payment success / fresh upload replaces the list). */
export async function removeAllCiplFiles() {
  try {
    const db = await getDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* no-op */
  }
}

/**
 * Re-fill empty `*_file` fields from IndexedDB before sending to the create API.
 * Returns a copy WITHOUT the internal `fileKey` — it must never reach the API
 * payload. Legacy rows (no fileKey yet, fields already populated) pass through
 * unchanged. The live row is untouched (copy, not delete) so edit hydration and
 * delete-drop can still use its fileKey.
 */
export async function attachInterpackDocumentFiles(doc) {
  if (!doc) return doc;
  const { fileKey, ...rest } = doc;
  let stored = {};
  if (fileKey) stored = await getCiplFiles(fileKey);
  for (const f of FILE_FIELDS) {
    if (!rest[f] && stored[f]) rest[f] = stored[f];
  }
  return rest;
}
